import { useEffect, useState } from 'react';
import { useNavigate, useParams, Outlet, Link, useLocation } from 'react-router-dom';
import {
    Search,
    Package,
    BarChart3,
    Settings,
    LogOut,
    Home,
    ExternalLink,
    Store
} from 'lucide-react';
import { getClient } from '../api/client';
import { ThemeToggle } from '../components/ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
import type { Client } from '../types';
import styles from './Dashboard.module.css';

export function DashboardLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const { clientId } = useParams<{ clientId: string }>();
    const { clientUser, loading, signOut } = useAuth();
    const [client, setClient] = useState<Client | null>(null);

    useEffect(() => {
        // Wait for auth to load
        if (loading) return;

        // Check auth
        if (!clientUser) {
            navigate('/login');
            return;
        }

        // Verify client access
        if (clientId && clientUser.clientId !== clientId) {
            navigate('/login');
            return;
        }

        // Load client data
        if (clientId) {
            getClient(clientId).then(setClient).catch(console.error);
        }
    }, [clientId, clientUser, loading, navigate]);

    const handleLogout = async () => {
        await signOut();
        navigate('/login');
    };

    const isActive = (path: string) => {
        return location.pathname === `/${clientId}${path}` ||
            location.pathname === `/${clientId}${path}/`;
    };

    if (loading || !clientUser) {
        return null;
    }

    const menuItems = [
        { path: '', icon: Home, label: 'Dashboard', exact: true },
        { path: '/scentmatch', icon: Search, label: 'ScentMatch' },
        { path: '/catalog', icon: Package, label: 'Catalog' },
        { path: '/analytics', icon: BarChart3, label: 'Analytics' },
        { path: '/settings', icon: Settings, label: 'Settings' },
    ];

    return (
        <div className={styles.layout}>
            {/* Sidebar */}
            <aside className={styles.sidebar}>
                <div className={styles.sidebarHeader}>
                    <Store className={styles.storeIcon} size={24} />
                    <div>
                        <h2>{client?.client_name || clientUser.clientName}</h2>
                        <p>{clientUser.email}</p>
                    </div>
                </div>

                <nav className={styles.nav}>
                    {menuItems.map((item) => {
                        const active = item.exact
                            ? location.pathname === `/${clientId}` || location.pathname === `/${clientId}/`
                            : isActive(item.path);

                        return (
                            <Link
                                key={item.path}
                                to={`/${clientId}${item.path}`}
                                className={`${styles.navItem} ${active ? styles.active : ''}`}
                            >
                                <item.icon size={20} />
                                <span>{item.label}</span>
                            </Link>
                        );
                    })}
                </nav>

                <div className={styles.sidebarFooter}>
                    <a
                        href={`/k/${clientId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.kioskLink}
                    >
                        <ExternalLink size={18} />
                        <span>Buka Kiosk Pelanggan</span>
                    </a>

                    <div className={styles.footerActions}>
                        <ThemeToggle />
                        <button onClick={handleLogout} className={styles.logoutBtn}>
                            <LogOut size={18} />
                            <span>Logout</span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className={styles.main}>
                <Outlet />
            </main>
        </div>
    );
}

// Dashboard Home
export function DashboardHome() {
    const { clientId } = useParams<{ clientId: string }>();
    const navigate = useNavigate();

    const features = [
        {
            icon: '🔍',
            title: 'ScentMatch',
            description: 'AI-powered perfume search dan rekomendasi untuk pelanggan',
            path: '/scentmatch',
            color: '#8b5cf6'
        },
        {
            icon: '📦',
            title: 'Product Catalog',
            description: 'Kelola katalog produk parfum Anda',
            path: '/catalog',
            color: '#06b6d4'
        },
        {
            icon: '📊',
            title: 'Analytics',
            description: 'Insight dari pencarian dan preferensi pelanggan',
            path: '/analytics',
            color: '#22c55e'
        },
        {
            icon: '⚙️',
            title: 'Settings',
            description: 'Pengaturan toko dan akun',
            path: '/settings',
            color: '#f59e0b'
        },
    ];

    return (
        <div className={styles.dashboard}>
            <header className={styles.header}>
                <h1>Selamat Datang! 👋</h1>
                <p>Kelola toko parfum Anda dengan mudah</p>
            </header>

            <div className={styles.featuresGrid}>
                {features.map((feature) => (
                    <div
                        key={feature.path}
                        className={styles.featureCard}
                        onClick={() => navigate(`/${clientId}${feature.path}`)}
                        style={{ '--accent-color': feature.color } as React.CSSProperties}
                    >
                        <span className={styles.featureIcon}>{feature.icon}</span>
                        <h3>{feature.title}</h3>
                        <p>{feature.description}</p>
                    </div>
                ))}
            </div>

            <div className={styles.quickActions}>
                <h2>Quick Actions</h2>
                <div className={styles.actionButtons}>
                    <a
                        href={`/k/${clientId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.actionBtn}
                    >
                        🏪 Buka Kiosk Pelanggan
                    </a>
                    <button
                        className={styles.actionBtn}
                        onClick={() => navigate(`/${clientId}/catalog`)}
                    >
                        ➕ Tambah Produk
                    </button>
                    <button
                        className={styles.actionBtn}
                        onClick={() => navigate(`/${clientId}/analytics`)}
                    >
                        📈 Lihat Analytics
                    </button>
                </div>
            </div>
        </div>
    );
}
